import { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { widget } from '../charting_library'; // same path you already use
import FuturesDatafeed from './datafeedFutures';
import './index.css';
import { ProfileContext } from '../../../context/ProfileProvider';
import { SocketContext } from '../../SocketContext';
import { disconnectFuturesChartSocket, setSharedSocket } from './streamingFutures';
import {
  bindChartAutoSave,
  CHART_STATE_KEYS,
  loadChartState,
} from '../tvChartStateStorage';
import {
  applyChartThemeToWidget,
  CHART_BG_DARK,
  CHART_BG_LIGHT,
  getChartPaneOverrides,
  getChartThemeFromBody,
} from '../chartThemeFromBody';

const TV_FUTURES_CHART_CONTAINER = '#TVFuturesChartContainer';

/**
 * Usage:
 *  <TVFuturesChartContainer symbol="BTCUSDT_PERP" />
 *  (Symbol format: BASEQUOTE_PERP for perpetuals, e.g., ETHUSDT_PERP)
 */
export default function TVFuturesChartContainer({ symbol }) {
  const { newStoredTheme } = useContext(ProfileContext);
  const { getSocket, isConnected } = useContext(SocketContext);
  const [tvWidget, setTvWidget] = useState();
  const initOnceRef = useRef(false);
  const tvWidgetRef = useRef(null);
  const flushChartSaveRef = useRef(null);
  tvWidgetRef.current = tvWidget;

  // Use same socket as UsdMFutures (futures:update already subscribed there)
  useEffect(() => {
    if (isConnected) {
      const socket = getSocket();
      if (socket) setSharedSocket(socket);
    }
  }, [isConnected, getSocket]);

  const initChart = useCallback((symbol) => {
    const containerId = 'TVFuturesChartContainer';
    const container = document.getElementById(containerId);
    if (!container) {
      setTimeout(() => initChart(symbol), 300);
      return;
    }

    const Theme = getChartThemeFromBody();
    const isMobile = window.innerWidth <= 480;
    const bgColor = Theme === 'light' ? CHART_BG_LIGHT : CHART_BG_DARK;
    const savedData = loadChartState(CHART_STATE_KEYS.futures);
    const options = {
      symbol,
      interval: '1',
      container: containerId,
      datafeed: FuturesDatafeed, // <= FUTURES DATAFEED
      library_path: '/charting_library/',
      timezone: 'Asia/Kolkata',
      has_intraday: true,
      intraday_multipliers: ['1', '60'],
      height: isMobile ? '400px' : '625px',
      load_last_chart: true,
      auto_save_delay: 1,
      ...(savedData ? { saved_data: savedData } : {}),
      fullscreen: false,
      pricescale: 100000000,
      custom_css_url: 'css/style.css',
      hide_resolution_in_legend: true,
      time_frames: [
        { text: '1D', resolution: 'D', description: '1 Day' },
        { text: '1W', resolution: 'W', description: '1 Week' },
        { text: '1M', resolution: 'M', description: '1 Month' },
      ],
      time_scale: { min_bar_spacing: 1 },
      theme: Theme === 'light' ? 'light' : 'dark',
      disabled_features: [
        'use_sessionStorage_for_settings', 'adaptive_logo',
        'border_around_the_chart', 'header_symbol_search',
        'header_interval_dialog_button', 'header_compare',
        'header_undo_redo', 'header_resolutions',
      ],
      overrides: {
        ...getChartPaneOverrides(Theme),
        'scalesProperties.fontSize': 14,
        'scalesProperties.fontFamily': 'HarmonyOS Sans Regular, sans-serif',
        'paneProperties.legendProperties.showLegend': true,
        'paneProperties.legendProperties.showStudyValues': true,
        'paneProperties.legendProperties.fontSize': 14,
        'paneProperties.legendProperties.fontFamily': 'HarmonyOS Sans Regular, sans-serif',
      },
      loading_screen: {
        backgroundColor: bgColor,
      },
    };

    const w = new widget(options);
    flushChartSaveRef.current = bindChartAutoSave(w, CHART_STATE_KEYS.futures);

    w.onChartReady(() => {
      try {
        const chart = w.chart?.();
        if (!chart) return;
        applyChartThemeToWidget(w, TV_FUTURES_CHART_CONTAINER);

        // Remove volume from chart
        const studies = chart.getAllStudies?.() || [];
        studies.forEach(study => {
          if (study?.name?.toLowerCase?.().includes('volume')) {
            chart.removeEntity(study.id);
          }
        });

        // Keep page symbol after restoring saved layout (drawings stay in layout)
        if (savedData && symbol) {
          try {
            chart.setSymbol(symbol, () => null);
          } catch (err) {}
        }

        // === Custom timeframe buttons ===
        w.headerReady?.().then(() => {
          try {
            const ch = w.chart?.();
            if (!ch) return;
            const intervals = [
          { value: '1', label: '1 Min' },
          { value: '5', label: '5 Min' },
          { value: '15', label: '15 Min' },
          { value: '30', label: '30 Min' },
          { value: '60', label: '1 Hour' },
          { value: 'D', label: '1 Day' },
          { value: 'W', label: '1 Week' },
          { value: 'M', label: '1 Month' },
        ];
            intervals.forEach(i => {
              const btn = w.createButton?.();
              if (!btn) return;
              btn.classList.add('custom-interval-button');
              btn.title = `Switch to ${i.label}`;
              btn.textContent = i.label;
              btn.addEventListener('click', () => {
                try {
                  w.chart?.()?.setResolution(i.value);
                } catch (err) {}
              });
            });
          } catch (err) {}
        });
      } catch (e) {
        // Chart API may not be ready
      }
    });
    

    setTvWidget(w);
  }, []);

  useEffect(() => {
    if (!initOnceRef.current && symbol) {
      initChart(symbol);
      initOnceRef.current = true;
    }
  }, [symbol, initChart]);

  // Cleanup only on unmount (not when symbol changes)
  useEffect(() => {
    return () => {
      try {
        flushChartSaveRef.current?.();
      } catch (e) {}
      disconnectFuturesChartSocket();
      const w = tvWidgetRef.current;
      if (w) {
        try {
          w.remove();
        } catch (e) {
          console.warn("Error removing futures TV widget:", e);
        }
      }
    };
  }, []);

  // theme switch
  useEffect(() => {
    if (!tvWidget) return;
    (async () => {
      try {
        await tvWidget._innerWindowLoaded;
        applyChartThemeToWidget(tvWidget, TV_FUTURES_CHART_CONTAINER);
      } catch (e) {
        console.error('Theme change failed:', e);
      }
    })();
  }, [newStoredTheme, tvWidget]);

  // change symbol live
  useEffect(() => {
    if (!tvWidget || !symbol) return;
    tvWidget.onChartReady(() => {
      try {
        const chart = tvWidget.chart?.();
        if (chart) chart.setSymbol(symbol, () => null);
      } catch (e) {
        // Widget may have been removed or chart not ready
      }
    });
  }, [symbol, tvWidget]);

  const isMobile = window.innerWidth <= 480;
  const chartHeight = isMobile ? '400px' : '625px';
  const Theme = localStorage.getItem('app_theme');
  const bgColor = Theme === 'light' ? '#ffffff' : '#181A20';

  return (
    <div
      style={{
        position: "relative",
        minHeight: chartHeight,
        height: chartHeight,
        width: '100%',
        backgroundColor: bgColor,
      }}
    >
      <div
        id="TVFuturesChartContainer"
        style={{
          width: '100%',
          height: chartHeight,
          minHeight: chartHeight,
          backgroundColor: bgColor,
        }}
      />
    </div>
  );
}
